<script lang="ts">
import { RouteNames } from "../../../../utils/constants.ts";

export function getRouteXAccountsCardOrder() {
  return {
    name: RouteNames.X_CARD_ORDER,
  };
}
</script>
<script setup lang="ts">
import BreadCrumbs from "../../../../components/BreadCrumbs.vue";
import BasicCard from "../../../../components/BasicCard.vue";
import TextInput from "../../../../components/form/TextInput.vue";
import InputSelect from "../../../../components/form/InputSelect.vue";
import InputCheckbox from "../../../../components/form/InputCheckbox.vue";
import { ref, computed } from "vue";
import { 
  ArrowRight as ArrowRightIcon,
  CreditCard as CreditCardIcon,
  Check as CheckIcon,
  AlertTriangle as AlertTriangleIcon,
  Shield as ShieldIcon,
  Truck as TruckIcon,
  Zap as ZapIcon,
  Lock as LockIcon
} from "lucide-vue-next";

// Card types
const cardTypes = {
  "virtual": "Virtual Card - Instant, Online Only",
  "physical": "Physical Card - 5-7 days delivery",
  "premium": "Premium Card - Metal, 3-5 days delivery"
};

// Card designs
const cardDesigns = [
  { id: "classic_black", name: "Classic Black", preview: "bg-gradient-to-r from-gray-800 to-black" },
  { id: "midnight_blue", name: "Midnight Blue", preview: "bg-gradient-to-r from-blue-800 to-blue-900" },
  { id: "royal_purple", name: "Royal Purple", preview: "bg-gradient-to-r from-purple-600 to-purple-800" },
  { id: "sunset_orange", name: "Sunset Orange", preview: "bg-gradient-to-r from-orange-500 to-red-600" },
  { id: "forest_green", name: "Forest Green", preview: "bg-gradient-to-r from-green-600 to-green-800" },
  { id: "rose_gold", name: "Rose Gold", preview: "bg-gradient-to-r from-pink-400 to-yellow-400" }
];

// Countries for shipping
const countries = {
  "US": "United States",
  "CA": "Canada",
  "GB": "United Kingdom", 
  "DE": "Germany",
  "FR": "France",
  "IT": "Italy",
  "ES": "Spain",
  "AU": "Australia",
  "JP": "Japan",
  "CH": "Switzerland",
  "NL": "Netherlands",
  "SE": "Sweden",
  "NO": "Norway"
};

// Form state
const cardType = ref("virtual");
const selectedDesign = ref("classic_black");
const cardholderName = ref("");
const linkedAccount = ref("checking_usd");
const expeditedShipping = ref(false);
const expressDelivery = ref(false);

// Shipping address
const shippingAddress = ref({
  firstName: "",
  lastName: "",
  addressLine1: "",
  addressLine2: "",
  city: "",
  state: "",
  zipCode: "",
  country: "US"
});

// Card settings
const dailyLimit = ref("1000");
const monthlyLimit = ref("5000");
const enableContactless = ref(true);
const enableInternational = ref(true);
const enableOnlineTransactions = ref(true);
const enableATMWithdrawals = ref(true);
const cardPIN = ref("");

// UI state
const step = ref(1); // 1: card type & design, 2: personal info, 3: shipping, 4: settings, 5: review, 6: confirmation
const isLoading = ref(false);
const orderNumber = ref("");

// Account options
const accountOptions = {
  "checking_usd": "Checking Account (USD) - $5,420.50",
  "savings_usd": "Savings Account (USD) - $12,340.00",
  "business_usd": "Business Account (USD) - $25,680.00"
};

// Helper functions for card pricing
const getCardFee = (type: string) => {
  const fees = {
    "virtual": 0,
    "physical": 15,
    "premium": 99
  };
  return fees[type as keyof typeof fees] || 0;
};

const getDeliveryTime = (type: string) => {
  if (type === "virtual") return "Instant";
  if (type === "premium") return "3-5 business days";
  return "5-7 business days";
};

// Computed values
const cardFee = computed(() => {
  return getCardFee(cardType.value);
});

const shippingFee = computed(() => {
  if (cardType.value === "virtual") return 0;
  let fee = 0;
  if (expeditedShipping.value) fee += 25;
  if (expressDelivery.value) fee += 50;
  return fee;
});

const totalCost = computed(() => {
  return cardFee.value + shippingFee.value;
});

const deliveryTime = computed(() => {
  if (cardType.value === "virtual") return "Instant";
  if (expressDelivery.value) return "1-2 business days";
  if (expeditedShipping.value) return "3-5 business days";
  if (cardType.value === "premium") return "3-5 business days";
  return "5-7 business days";
});

const selectedDesignData = computed(() => {
  return cardDesigns.find(d => d.id === selectedDesign.value) || cardDesigns[0];
});

// Validation
const validateStep1 = () => {
  return cardType.value && selectedDesign.value;
};

const validateStep2 = () => {
  return cardholderName.value.trim().length >= 2 && linkedAccount.value;
};

const validateStep3 = () => {
  if (cardType.value === "virtual") return true;
  const addr = shippingAddress.value;
  return addr.firstName && addr.lastName && addr.addressLine1 && 
         addr.city && addr.state && addr.zipCode && addr.country;
};

const validateStep4 = () => {
  return dailyLimit.value && monthlyLimit.value && 
         parseFloat(dailyLimit.value) <= parseFloat(monthlyLimit.value);
};

// Form handlers
const nextStep = () => {
  if (step.value === 1 && validateStep1()) {
    step.value = 2;
  } else if (step.value === 2 && validateStep2()) {
    step.value = cardType.value === "virtual" ? 4 : 3; // Skip shipping for virtual cards
  } else if (step.value === 3 && validateStep3()) {
    step.value = 4;
  } else if (step.value === 4 && validateStep4()) {
    step.value = 5;
  }
};

const previousStep = () => {
  if (step.value > 1) {
    if (step.value === 4 && cardType.value === "virtual") {
      step.value = 2; // Skip shipping step for virtual cards
    } else {
      step.value--;
    }
  }
};

const confirmOrder = async () => {
  isLoading.value = true;
  
  // Simulate API call
  setTimeout(() => {
    orderNumber.value = "ORD" + Math.random().toString(36).substring(2, 11).toUpperCase();
    step.value = 6;
    isLoading.value = false;
  }, 3000);
};

const startNewOrder = () => {
  step.value = 1;
  cardholderName.value = "";
  cardPIN.value = "";
  orderNumber.value = "";
  shippingAddress.value = {
    firstName: "",
    lastName: "",
    addressLine1: "",
    addressLine2: "",
    city: "",
    state: "",
    zipCode: "",
    country: "US"
  };
};

// Card features by type
const cardFeatures = {
  virtual: [
    "Instant activation",
    "Perfect for online shopping",
    "No physical delivery needed", 
    "Same security as physical cards",
    "No annual fee"
  ],
  physical: [
    "Contactless payments",
    "ATM withdrawals worldwide",
    "Chip & PIN security",
    "Works everywhere Visa is accepted",
    "5-7 day delivery"
  ],
  premium: [
    "Metal card with premium feel",
    "Priority customer support",
    "Higher transaction limits",
    "Travel insurance included",
    "Airport lounge access"
  ]
};
</script>

<template>
  <div class="px-4 md:px-0">
    <BreadCrumbs :title="$t('Order Card')" class="mb-4 md:mb-6" />

    <div class="max-w-4xl mx-auto">
    <!-- Step Indicator -->
    <div class="mb-6 md:mb-8 overflow-x-auto pb-2">
      <div class="flex items-center justify-center space-x-1 sm:space-x-2 md:space-x-4 min-w-max px-4 md:px-0">
        <div class="flex items-center">
          <div :class="[step >= 1 ? 'bg-[var(--m-primary-base)] text-white' : 'bg-gray-300 text-gray-600', 'w-8 h-8 md:w-10 md:h-10 rounded-full flex items-center justify-center text-xs md:text-sm font-semibold transition-all']">
            1
          </div>
          <span class="ml-1.5 md:ml-2 text-xs md:text-sm font-medium hidden sm:inline">Card Type</span>
        </div>
        <arrow-right-icon class="h-3 w-3 md:h-4 md:w-4 text-gray-400 flex-shrink-0" />
        <div class="flex items-center">
          <div :class="[step >= 2 ? 'bg-[var(--m-primary-base)] text-white' : 'bg-gray-300 text-gray-600', 'w-8 h-8 md:w-10 md:h-10 rounded-full flex items-center justify-center text-xs md:text-sm font-semibold transition-all']">
            2
          </div>
          <span class="ml-1.5 md:ml-2 text-xs md:text-sm font-medium hidden sm:inline">Details</span>
        </div>
        <arrow-right-icon class="h-3 w-3 md:h-4 md:w-4 text-gray-400 flex-shrink-0" />
        <div class="flex items-center">
          <div :class="[step >= 3 ? 'bg-[var(--m-primary-base)] text-white' : 'bg-gray-300 text-gray-600', 'w-8 h-8 md:w-10 md:h-10 rounded-full flex items-center justify-center text-xs md:text-sm font-semibold transition-all']">
            3
          </div>
          <span class="ml-1.5 md:ml-2 text-xs md:text-sm font-medium hidden sm:inline">Shipping</span>
        </div>
        <arrow-right-icon class="h-3 w-3 md:h-4 md:w-4 text-gray-400 flex-shrink-0" />
        <div class="flex items-center">
          <div :class="[step >= 4 ? 'bg-[var(--m-primary-base)] text-white' : 'bg-gray-300 text-gray-600', 'w-8 h-8 md:w-10 md:h-10 rounded-full flex items-center justify-center text-xs md:text-sm font-semibold transition-all']">
            4
          </div>
          <span class="ml-1.5 md:ml-2 text-xs md:text-sm font-medium hidden sm:inline">Settings</span>
        </div>
        <arrow-right-icon class="h-3 w-3 md:h-4 md:w-4 text-gray-400 flex-shrink-0" />
        <div class="flex items-center">
          <div :class="[step >= 5 ? 'bg-[var(--m-primary-base)] text-white' : 'bg-gray-300 text-gray-600', 'w-8 h-8 md:w-10 md:h-10 rounded-full flex items-center justify-center text-xs md:text-sm font-semibold transition-all']">
            5
          </div>
          <span class="ml-1.5 md:ml-2 text-xs md:text-sm font-medium hidden sm:inline">Review</span>
        </div>
        <arrow-right-icon class="h-3 w-3 md:h-4 md:w-4 text-gray-400 flex-shrink-0" />
        <div class="flex items-center">
          <div :class="[step >= 6 ? 'bg-green-500 text-white' : 'bg-gray-300 text-gray-600', 'w-8 h-8 md:w-10 md:h-10 rounded-full flex items-center justify-center text-xs md:text-sm font-semibold transition-all']">
            <check-icon v-if="step >= 6" class="h-4 w-4 md:h-5 md:w-5" />
            <span v-else>6</span>
          </div>
          <span class="ml-1.5 md:ml-2 text-xs md:text-sm font-medium hidden sm:inline">Done</span>
        </div>
      </div>
    </div>

    <!-- Step 1: Card Type & Design -->
    <div v-if="step === 1" class="space-y-6">
      <!-- Card Type Selection -->
      <BasicCard>
        <div class="flex items-center gap-3 mb-6">
          <credit-card-icon class="h-6 w-6 text-blue-600" />
          <h2 class="text-xl font-semibold">Choose Your Card</h2>
        </div>

        <div class="space-y-4">
          <div
            v-for="(description, type) in cardTypes"
            :key="type"
            @click="cardType = type"
            :class="[
              'p-4 border rounded-lg cursor-pointer transition-all',
              cardType === type ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300'
            ]"
          >
            <div class="flex justify-between items-start">
              <div class="flex-1">
                <h3 class="font-medium capitalize">{{ type }} Card</h3>
                <p class="text-sm text-gray-600 mt-1">{{ description }}</p>
                <div class="mt-3">
                  <h4 class="text-sm font-medium mb-2">Features:</h4>
                  <ul class="text-sm text-gray-600 space-y-1">
                    <li v-for="feature in cardFeatures[type as keyof typeof cardFeatures]" :key="feature" class="flex items-center gap-2">
                      <check-icon class="h-3 w-3 text-green-600" />
                      {{ feature }}
                    </li>
                  </ul>
                </div>
              </div>
              <div class="text-right ml-4">
                <div class="text-lg font-semibold">
                  {{ getCardFee(type) === 0 ? 'Free' : `$${getCardFee(type)}` }}
                </div>
                <div class="text-sm text-gray-500">{{ getDeliveryTime(type) }}</div>
              </div>
            </div>
          </div>
        </div>
      </BasicCard>

      <!-- Card Design Selection -->
      <BasicCard v-if="cardType !== 'virtual'">
        <h3 class="text-lg font-semibold mb-4 md:mb-6">Choose Design</h3>
        <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4">
          <div
            v-for="design in cardDesigns"
            :key="design.id"
            @click="selectedDesign = design.id"
            :class="[
              'relative aspect-[1.6/1] rounded-xl cursor-pointer transition-all overflow-hidden shadow-sm hover:shadow-md',
              selectedDesign === design.id ? 'ring-2 ring-[var(--m-primary-base)] ring-offset-2 scale-105' : 'hover:scale-[1.02]'
            ]"
          >
            <div :class="['w-full h-full flex items-center justify-center text-white font-medium', design.preview]">
              <div class="text-center">
                <div class="text-xs md:text-sm opacity-80 font-semibold tracking-wider">MONETERUM</div>
                <div class="text-xl md:text-2xl mt-2">••••</div>
              </div>
            </div>
            <div class="absolute bottom-2 md:bottom-3 left-2 md:left-3 text-xs md:text-sm text-white font-medium drop-shadow">
              {{ design.name }}
            </div>
            <div v-if="selectedDesign === design.id" class="absolute top-2 md:top-3 right-2 md:right-3">
              <check-icon class="h-5 w-5 md:h-6 md:w-6 text-white bg-[var(--m-primary-base)] rounded-full p-1" />
            </div>
          </div>
        </div>
      </BasicCard>

      <!-- Action Button -->
      <div class="flex justify-end pt-4">
        <button
          @click="nextStep"
          :disabled="!validateStep1()"
          class="w-full md:w-auto px-6 md:px-8 py-3 bg-[var(--m-primary-base)] text-white rounded-lg hover:opacity-90 disabled:bg-gray-300 disabled:cursor-not-allowed transition-all font-medium"
        >
          Continue
        </button>
      </div>
    </div>

    <!-- Step 2: Personal Information -->
    <BasicCard v-if="step === 2">
      <div class="space-y-6">
        <div class="flex items-center gap-3 mb-6">
          <shield-icon class="h-6 w-6 text-green-600" />
          <h2 class="text-xl font-semibold">Personal Information</h2>
        </div>

        <form @submit.prevent="nextStep" class="space-y-6">
          <div>
            <TextInput
              v-model="cardholderName"
              name="cardholderName"
              type="text"
              label="Cardholder Name"
              placeholder="Full name as it appears on your ID"
            />
            <p class="text-xs text-gray-500 mt-1">
              This name will be printed on your card and must match your legal name.
            </p>
          </div>

          <div>
            <label class="text-sm text-gray-600 mb-2 block">Link to Account</label>
            <InputSelect 
              v-model="linkedAccount" 
              :list="accountOptions"
              placeholder="Select account to link"
            />
          </div>

          <div v-if="cardType !== 'virtual'">
            <TextInput
              v-model="cardPIN"
              name="cardPIN"
              type="password"
              label="Card PIN (4 digits)"
              placeholder="1234"
              maxlength="4"
            />
            <p class="text-xs text-gray-500 mt-1">
              You'll use this PIN for ATM withdrawals and chip transactions.
            </p>
          </div>

          <!-- Action Buttons -->
          <div class="flex flex-col-reverse sm:flex-row justify-between gap-3 pt-6">
            <button
              type="button"
              @click="previousStep"
              class="px-6 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors font-medium"
            >
              Back
            </button>
            <button
              type="submit"
              :disabled="!validateStep2()"
              class="px-6 md:px-8 py-3 bg-[var(--m-primary-base)] text-white rounded-lg hover:opacity-90 disabled:bg-gray-300 disabled:cursor-not-allowed transition-all font-medium"
            >
              Continue
            </button>
          </div>
        </form>
      </div>
    </BasicCard>

    <!-- Step 3: Shipping Address (Physical Cards Only) -->
    <BasicCard v-if="step === 3 && cardType !== 'virtual'">
      <div class="space-y-6">
        <div class="flex items-center gap-3 mb-6">
          <truck-icon class="h-6 w-6 text-orange-600" />
          <h2 class="text-xl font-semibold">Shipping Address</h2>
        </div>

        <form @submit.prevent="nextStep" class="space-y-4 md:space-y-6">
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <TextInput
              v-model="shippingAddress.firstName"
              name="firstName"
              type="text"
              label="First Name"
              placeholder="John"
            />
            <TextInput
              v-model="shippingAddress.lastName"
              name="lastName"
              type="text"
              label="Last Name"
              placeholder="Doe"
            />
          </div>

          <div>
            <TextInput
              v-model="shippingAddress.addressLine1"
              name="addressLine1"
              type="text"
              label="Address Line 1"
              placeholder="123 Main Street"
            />
          </div>

          <div>
            <TextInput
              v-model="shippingAddress.addressLine2"
              name="addressLine2"
              type="text"
              label="Address Line 2 (Optional)"
              placeholder="Apt 4B"
            />
          </div>

          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <TextInput
              v-model="shippingAddress.city"
              name="city"
              type="text"
              label="City"
              placeholder="New York"
            />
            <TextInput
              v-model="shippingAddress.state"
              name="state"
              type="text"
              label="State/Province"
              placeholder="NY"
            />
          </div>

          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <TextInput
              v-model="shippingAddress.zipCode"
              name="zipCode"
              type="text"
              label="ZIP/Postal Code"
              placeholder="10001"
            />
            <div>
              <label class="text-sm text-gray-600 mb-2 block">Country</label>
              <InputSelect
                v-model="shippingAddress.country"
                :list="countries"
              />
            </div>
          </div>

          <!-- Shipping Options -->
          <div class="space-y-3">
            <h3 class="font-medium">Shipping Options</h3>
            <InputCheckbox v-model="expeditedShipping" name="expedited">
              Expedited Shipping (+$25) - 3-5 business days
            </InputCheckbox>
            <InputCheckbox v-model="expressDelivery" name="express">
              Express Delivery (+$50) - 1-2 business days
            </InputCheckbox>
          </div>

          <!-- Action Buttons -->
          <div class="flex flex-col-reverse sm:flex-row justify-between gap-3 pt-6">
            <button
              type="button"
              @click="previousStep"
              class="px-6 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors font-medium"
            >
              Back
            </button>
            <button
              type="submit"
              :disabled="!validateStep3()"
              class="px-6 md:px-8 py-3 bg-[var(--m-primary-base)] text-white rounded-lg hover:opacity-90 disabled:bg-gray-300 disabled:cursor-not-allowed transition-all font-medium"
            >
              Continue
            </button>
          </div>
        </form>
      </div>
    </BasicCard>

    <!-- Step 4: Card Settings -->
    <BasicCard v-if="step === 4">
      <div class="space-y-6">
        <div class="flex items-center gap-3 mb-6">
          <lock-icon class="h-6 w-6 text-purple-600" />
          <h2 class="text-xl font-semibold">Card Settings</h2>
        </div>

        <form @submit.prevent="nextStep" class="space-y-4 md:space-y-6">
          <!-- Transaction Limits -->
          <div>
            <h3 class="font-medium mb-3 md:mb-4">Transaction Limits</h3>
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <TextInput
                  v-model="dailyLimit"
                  name="dailyLimit"
                  type="number"
                  label="Daily Limit ($)"
                  placeholder="1000"
                />
              </div>
              <div>
                <TextInput
                  v-model="monthlyLimit"
                  name="monthlyLimit"
                  type="number"
                  label="Monthly Limit ($)"
                  placeholder="5000"
                />
              </div>
            </div>
          </div>

          <!-- Card Features -->
          <div>
            <h3 class="font-medium mb-4">Card Features</h3>
            <div class="space-y-3">
              <InputCheckbox v-model="enableContactless" name="contactless">
                Enable contactless payments (Tap to pay)
              </InputCheckbox>
              <InputCheckbox v-model="enableOnlineTransactions" name="online">
                Enable online transactions
              </InputCheckbox>
              <InputCheckbox v-model="enableInternational" name="international">
                Enable international transactions
              </InputCheckbox>
              <InputCheckbox v-model="enableATMWithdrawals" name="atm">
                Enable ATM withdrawals
              </InputCheckbox>
            </div>
          </div>

          <!-- Action Buttons -->
          <div class="flex flex-col-reverse sm:flex-row justify-between gap-3 pt-6">
            <button
              type="button"
              @click="previousStep"
              class="px-6 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors font-medium"
            >
              Back
            </button>
            <button
              type="submit"
              :disabled="!validateStep4()"
              class="px-6 md:px-8 py-3 bg-[var(--m-primary-base)] text-white rounded-lg hover:opacity-90 disabled:bg-gray-300 disabled:cursor-not-allowed transition-all font-medium"
            >
              Continue
            </button>
          </div>
        </form>
      </div>
    </BasicCard>

    <!-- Step 5: Review Order -->
    <BasicCard v-if="step === 5">
      <div class="space-y-6">
        <div class="flex items-center gap-3 mb-6">
          <alert-triangle-icon class="h-6 w-6 text-orange-600" />
          <h2 class="text-xl font-semibold">Review Your Order</h2>
        </div>

        <!-- Card Preview -->
        <div class="bg-gray-50 p-6 rounded-lg">
          <div class="flex items-center gap-4 mb-4">
            <div :class="['w-16 h-10 rounded flex items-center justify-center text-white text-xs font-medium', selectedDesignData.preview]">
              CARD
            </div>
            <div>
              <h3 class="font-medium capitalize">{{ cardType }} Card</h3>
              <p class="text-sm text-gray-600">{{ selectedDesignData.name }} Design</p>
            </div>
          </div>
        </div>

        <!-- Order Summary -->
        <div class="space-y-4">
          <div class="p-4 bg-gray-50 rounded-lg">
            <h3 class="font-medium text-gray-800 mb-3">Card Details</h3>
            <div class="space-y-2 text-sm">
              <div class="flex justify-between">
                <span>Cardholder Name</span>
                <span class="font-medium">{{ cardholderName }}</span>
              </div>
              <div class="flex justify-between">
                <span>Linked Account</span>
                <span class="font-medium">{{ accountOptions[linkedAccount as keyof typeof accountOptions] }}</span>
              </div>
              <div class="flex justify-between">
                <span>Daily Limit</span>
                <span class="font-medium">${{ dailyLimit }}</span>
              </div>
              <div class="flex justify-between">
                <span>Monthly Limit</span>
                <span class="font-medium">${{ monthlyLimit }}</span>
              </div>
            </div>
          </div>

          <div v-if="cardType !== 'virtual'" class="p-4 bg-gray-50 rounded-lg">
            <h3 class="font-medium text-gray-800 mb-3">Shipping Address</h3>
            <div class="text-sm">
              <p>{{ shippingAddress.firstName }} {{ shippingAddress.lastName }}</p>
              <p>{{ shippingAddress.addressLine1 }}</p>
              <p v-if="shippingAddress.addressLine2">{{ shippingAddress.addressLine2 }}</p>
              <p>{{ shippingAddress.city }}, {{ shippingAddress.state }} {{ shippingAddress.zipCode }}</p>
              <p>{{ countries[shippingAddress.country as keyof typeof countries] }}</p>
            </div>
          </div>

          <div class="p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <h3 class="font-medium text-blue-800 mb-3">Cost Breakdown</h3>
            <div class="space-y-2 text-sm">
              <div class="flex justify-between">
                <span>Card Fee</span>
                <span>${{ cardFee.toFixed(2) }}</span>
              </div>
              <div v-if="shippingFee > 0" class="flex justify-between">
                <span>Shipping Fee</span>
                <span>${{ shippingFee.toFixed(2) }}</span>
              </div>
              <div class="border-t pt-2 flex justify-between font-medium">
                <span>Total</span>
                <span>${{ totalCost.toFixed(2) }}</span>
              </div>
            </div>
          </div>

          <div class="bg-green-50 border border-green-200 rounded-lg p-4">
            <div class="flex items-start gap-3">
              <zap-icon class="h-5 w-5 text-green-600 mt-0.5" />
              <div>
                <h3 class="font-medium text-green-800 mb-1">Delivery Timeline</h3>
                <p class="text-sm text-green-700">
                  Your {{ cardType }} card will be {{ cardType === 'virtual' ? 'activated' : 'delivered' }} {{ deliveryTime.toLowerCase() }}.
                </p>
              </div>
            </div>
          </div>
        </div>

        <!-- Action Buttons -->
        <div class="flex flex-col-reverse sm:flex-row justify-between gap-3 pt-6">
          <button
            @click="previousStep"
            class="px-6 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors font-medium"
          >
            Back
          </button>
          <button
            @click="confirmOrder"
            :disabled="isLoading"
            class="px-6 md:px-8 py-3 bg-[var(--m-primary-base)] text-white rounded-lg hover:opacity-90 disabled:bg-gray-300 disabled:cursor-not-allowed transition-all font-medium"
          >
            <span v-if="isLoading">Processing Order...</span>
            <span v-else>Confirm Order</span>
          </button>
        </div>
      </div>
    </BasicCard>

    <!-- Step 6: Order Confirmation -->
    <BasicCard v-if="step === 6">
      <div class="text-center space-y-6">
        <div class="flex justify-center">
          <div class="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
            <check-icon class="h-8 w-8 text-green-600" />
          </div>
        </div>

        <div>
          <h2 class="text-2xl font-semibold text-green-800 mb-2">Order Confirmed!</h2>
          <p class="text-gray-600">
            Your {{ cardType }} card order has been submitted successfully.
          </p>
        </div>

        <div class="bg-gray-50 p-4 rounded-lg">
          <p class="text-sm text-gray-600 mb-2">Order Number</p>
          <code class="text-sm font-mono bg-white p-2 rounded border block">
            {{ orderNumber }}
          </code>
        </div>

        <div class="space-y-2">
          <p class="font-medium">{{ cardType === 'virtual' ? 'Card activated' : 'Card ordered' }} for {{ cardholderName }}</p>
          <p class="text-sm text-gray-600">Total cost: ${{ totalCost.toFixed(2) }}</p>
          <p class="text-sm text-gray-500">
            <span v-if="cardType === 'virtual'">Your virtual card is ready to use!</span>
            <span v-else>Expected delivery: {{ deliveryTime }}</span>
          </p>
        </div>

        <div class="flex flex-col sm:flex-row justify-center gap-3 sm:gap-4 pt-6">
          <button
            @click="startNewOrder"
            class="px-6 py-3 bg-[var(--m-primary-base)] text-white rounded-lg hover:opacity-90 transition-all font-medium"
          >
            Order Another Card
          </button>
          <button
            @click="$router.push({ name: 'account.cards' })"
            class="px-6 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors font-medium"
          >
            View My Cards
          </button>
        </div>
      </div>
    </BasicCard>
    </div>
  </div>
</template>

<style scoped lang="scss">

</style>
